import { useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { getAppointments, cancelAppointment } from "../../../../services/StudentPortal/StudentAppointmentService";
import { handleApiError } from "../../../../utils/errorHandler";
import { unwrapList } from "../../utils/studentPortalUtils";

// student/parent/appointments is gated server-side by the
// `parent.app.feature:appointment` middleware (see StudentAppointmentService.js),
// so an organization that hasn't enabled the feature can 403/404 this GET -
// that's an expected "not available for this school" state, not a failure,
// so it should never pop an error toast (same pattern as Timetable's
// isNoRecordsFound in useStudentTimetable.js).
const isFeatureGated = (error) => {
  const status = error?.response?.status;
  return status === 403 || status === 404;
};

/**
 * Data + actions for the student "Appointments" page: the appointment list
 * plus the cancel mutation. Neither the list response shape nor the cancel
 * response is confirmed against a real payload yet, so the page itself
 * (StudentAppointments.js) stays defensive about individual fields - this
 * hook only owns fetch/cancel/loading/feature-availability state.
 */
export const useStudentAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFeatureUnavailable, setIsFeatureUnavailable] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);

  const fetchAppointments = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getAppointments();
      setAppointments(unwrapList(response));
      setIsFeatureUnavailable(false);
    } catch (error) {
      if (isFeatureGated(error)) {
        setIsFeatureUnavailable(true);
        setAppointments([]);
      } else {
        handleApiError(error);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const cancel = useCallback(
    async (appointmentId) => {
      if (!appointmentId) return false;
      setCancellingId(appointmentId);
      try {
        await cancelAppointment(appointmentId);
        enqueueSnackbar("Appointment cancelled", { variant: "success" });
        await fetchAppointments();
        return true;
      } catch (error) {
        handleApiError(error);
        return false;
      } finally {
        setCancellingId(null);
      }
    },
    [fetchAppointments]
  );

  return { appointments, isLoading, isFeatureUnavailable, cancellingId, fetchAppointments, cancel };
};

export default useStudentAppointments;
